<template>
	<Headroom :offset="20" @pin="onHeaderPin" @unpin="onHeaderUnpin">
		<header class="main-header relative z-50 flex h-20 flex-wrap">
			<div
				class="main-header--nav relative z-20 flex w-full justify-between bg-white px-5 duration-200 xl:bg-gray-50"
			>
				<div class="flex items-center gap-3">
					<NuxtLink
						href="/"
						:class="[showMobileMenu ? 'lg:flex' : '']"
						@click="hideMobileMenu"
					>
						<IconCarbarLogo />
					</NuxtLink>
					<NuxtLink
						:href="whitelabel.links?.partnerUrl"
						:class="[showMobileMenu ? 'lg:flex' : '']"
						@click="hideMobileMenu"
					>
						<CarbarImage
							:image="whitelabel?.partnerLogo"
							:alt-text="whitelabel?.partnerLogoAltText"
							image-width-class="relative w-auto"
							image-height-class="h-[54px]"
						/>
					</NuxtLink>
				</div>
				<div v-if="showMenu" class="flex">
					<button
						class="hamburger hamburger--spring js-hamburger relative ml-auto flex items-center lg:hidden"
						:class="[showMobileMenu ? 'is-active' : '']"
						type="button"
						@click="toggleMobileMenu"
					>
						<span class="hamburger-box">
							<span class="hamburger-inner"></span>
						</span>
					</button>
				</div>

				<div
					v-if="showMenu"
					class="hidden items-center justify-center gap-2 lg:flex xl:flex-1"
				>
					<ul class="m-0 flex list-none gap-x-2">
						<li
							v-for="menuItem in menu"
							:key="menuItem.key"
							class="flex"
							:class="[menuItem.class || 'flex']"
						>
							<CarbarLink
								class="flex items-center px-2 xl:px-4"
								font-size="text-base"
								:href="menuItem.link"
								:target="menuItem.target"
								:underline="false"
								:font-weight="whitelabel.menuItemFontWeight"
							>
								<span :class="whitelabel.menuItemsColor">
									{{ menuItem.label }}
								</span>
							</CarbarLink>
						</li>
					</ul>

					<div v-if="isGroupSubscriptionEnabled" class="px-2 xl:px-4">
						<DropdownMoreMenu
							:menu-items="moreMenu"
							class="group cursor-pointer"
						>
							<template #menu-button>
								<span
									class="text-base font-semibold"
									:class="whitelabel.menuItemsColor"
								>
									More
								</span>
							</template>
							<template #caret>
								<div class="on-open-rotate-90 ml-1">
									<IconArrowDown
										class="text-gray-500 duration-200 group-hover:rotate-90"
										:class="whitelabel.iconColorHover"
									/>
								</div>
							</template>
						</DropdownMoreMenu>
					</div>
				</div>

				<div
					v-if="showMenu"
					class="hidden w-fit flex-1 items-center justify-end lg:flex lg:flex-none"
				>
					<div
						class="flex h-full items-center space-x-4 lg:space-x-3"
					>
						<div v-if="auUser.getIsSigningOut" class="h-4 w-4">
							<IconsIconLoading />
						</div>
						<DropdownUserMenu
							v-if="auUser.isLoggedIn"
							:menu-items="userMenu"
							class="group cursor-pointer"
						>
							<template #menu-button>
								<IconUser
									class="h-4 w-4 text-right text-gray-600 duration-200"
									:class="whitelabel.iconColorHover"
								/>
							</template>
							<template #caret>
								<div class="on-open-rotate-90 ml-1">
									<IconArrowDown
										class="text-gray-500 duration-200 group-hover:rotate-90"
										:class="whitelabel.iconColorHover"
									/>
								</div>
							</template>
						</DropdownUserMenu>
						<CarbarLink
							v-if="!auUser.isLoggedIn"
							:underline="false"
							:href="whitelabel?.links?.signInUrl"
						>
							<CarbarButton
								:is-full-width="false"
								:is-outline="true"
								:is-mixed="false"
								class="group flex flex-row items-center gap-2"
								:outline-color="
									whitelabel?.styles?.button?.outline
								"
								:text-color="
									whitelabel?.styles?.button?.outlineText
								"
								:border="
									whitelabel?.styles?.button?.borderRadius
								"
								:rounded="
									whitelabel.styles?.button?.borderRadius
								"
								@click="onLoginClick"
							>
								<IconUser
									class="h-3 w-3"
									:class="whitelabel?.styles?.button?.icon"
								/>
								<span :class="whitelabel?.menuItemFontWeight">
									Sign in
								</span>
							</CarbarButton>
						</CarbarLink>

						<CarbarLink
							v-if="!auUser.isLoggedIn"
							:href="whitelabel.links?.signUpUrl"
							:underline="false"
							class="flex h-fit items-center"
						>
							<CarbarButton
								:background-color="
									whitelabel.styles?.button?.bg
								"
								:rounded="
									whitelabel.styles?.button?.borderRadius
								"
								:font-weight="whitelabel?.menuItemFontWeight"
							>
								Join for free
							</CarbarButton>
						</CarbarLink>
					</div>
				</div>
			</div>
		</header>
		<div
			class="absolute inset-0 h-dvh w-full overflow-y-auto bg-white pb-10 pt-20 duration-200 lg:hidden"
			:class="[
				showMobileMenu
					? 'translate-x-0 opacity-100'
					: '-translate-x-full opacity-0',
			]"
		>
			<PartnerHeaderMobile :menu="menu" />
		</div>
	</Headroom>

	<div class="h-20"></div>
</template>

<script setup>
import IconArrowDown from '@/assets/svg/common/ico-arrow_down.svg'
import IconUser from '@/assets/svg/common/ico-user.svg'
import IconCarbarLogo from '@/assets/svg/logo-carbar-v3.svg'
import CarbarButton from '@/components/buttons/CarbarButton.vue'
import CarbarImage from '@/components/CarbarImage.vue'
import DropdownMoreMenu from '@/components/dropdowns/DropdownMoreMenu.vue'
import DropdownUserMenu from '@/components/dropdowns/DropdownUserMenu.vue'
import PartnerHeaderMobile from '@/components/headers/PartnerHeaderMobile.vue'
import CarbarLink from '@/components/links/CarbarLink.vue'
import { useCarbarStore } from '@/stores/carbar'
import { useCarbarAuAuthStore } from '@/stores/carbar-au-auth'
import Headroom from '@/vendor/headroom/headroom.vue'
import { headerModule } from '~/utils/Header.js'

const carbar = useCarbarStore()
const auUser = useCarbarAuAuthStore()

const showMobileMenu = computed(() => carbar.header.showMobileMenu)

const emit = defineEmits(['login', 'logout', 'join'])

const { toggleMobileMenu, hideMobileMenu } = headerModule()

const props = defineProps({
	showMenu: {
		type: Boolean,
		default: true,
		required: false,
	},
})

const { whitelabel } = useWhitelabelsStyles()

const isGroupSubscriptionEnabled = useIsGroupSubscriptionEnabled()

const menu = ref([
	{
		key: 'browse-cars',
		label: 'Browse cars',
		link: `${whitelabel.value.links.partnerUrl}/browse`,
		current: false,
		class: '',
	},
	...(isGroupSubscriptionEnabled
		? [
				{
					key: 'group-subscription',
					label: 'Group subscription',
					link: `${whitelabel.value.links.partnerUrl}/group-subscription`,
					current: false,
					class: '',
				},
			]
		: []),
	{
		key: 'how-it-works',
		label: 'How it works',
		link: `${whitelabel.value.links.partnerUrl}/how-it-works`,
		current: false,
		class: isGroupSubscriptionEnabled ? 'md:hidden xl:flex' : '',
	},
	{
		key: 'about-carbar',
		label: 'About carbar',
		link: `${whitelabel.value.links.partnerUrl}/about-us`,
		current: false,
		class: isGroupSubscriptionEnabled ? 'lg:hidden' : '',
	},
	{
		key: 'faq',
		label: 'FAQs',
		link: `${whitelabel.value.links.faqUrl}`,
		target: '_blank',
		current: false,
		class: isGroupSubscriptionEnabled ? 'lg:hidden' : '',
	},
])

const moreMenu = ref([
	{
		key: 'how-it-works',
		label: 'How it works',
		href: `${whitelabel.value.links.partnerUrl}/how-it-works`,
		class: 'xl:hidden',
	},
	{
		key: 'about-carbar',
		label: 'About carbar',
		href: `${whitelabel.value.links.partnerUrl}/about-us`,
	},
	{
		key: 'faq',
		label: 'FAQs',
		href: `${whitelabel.value.links.faqUrl}`,
		target: '_blank',
	},
])

const userMenu = ref([
	{
		label: 'Dashboard',
		href: '/dashboard',
	},
	{
		label: 'Account',
		href: '/dashboard/account',
	},
	{
		label: 'Help & support',
		link: `${whitelabel.value.links.faqUrl}`,
	},
	{
		label: 'Logout',
		onclick: () => {
			signOut()
		},
	},
])

const onHeaderPin = () => {
	document.body.classList.add(...['header-pinned'])
}

const onHeaderUnpin = () => {
	document.body.classList.remove(...['header-pinned'])
}

const onLoginClick = () => {
	emit('login')
}
</script>
